import type { FilterControl } from '../../../types/filterControl';

/** Facet row from filter-counts APIs (`value` + `count`). */
export type FilterFacetCount = {
  value: string;
  count: number;
};

/** Attach facet counts onto select options, boolean true/false and a checkbox. `countMap` is keyed by control `id`. */
export const enrichFiltersWithCounts = (
  controls: FilterControl[],
  countMap: Record<string, FilterFacetCount[] | undefined>
): FilterControl[] =>
  controls.map((control) => {
    const counts = countMap[control.id];
    if (!counts) {
      return control;
    }
    const countOf = (value: string) => counts.find((count) => count.value === value)?.count;
    switch (control.type) {
      case 'boolean':
        return { ...control, trueCount: countOf('true'), falseCount: countOf('false') };
      case 'checkbox':
        return { ...control, count: countOf('true') };
      case 'multi-select':
      case 'single-select':
        return {
          ...control,
          options: control.options.map((option) => {
            const count = countOf(String(option.value));
            return count === undefined ? option : { ...option, count };
          })
        };
      default:
        return control;
    }
  });

const rangeSet = (range: { from?: unknown; to?: unknown; min?: unknown; max?: unknown } | undefined) =>
  range && [range.from, range.to, range.min, range.max].some((bound) => bound !== undefined && bound !== null) ? 1 : 0;

/** Values set on a control, include and exclude both counted. */
export const getControlActiveCount = (control: FilterControl): number => {
  switch (control.type) {
    case 'multi-select':
      return control.value.include.length + control.value.exclude.length;
    case 'single-select':
      return (control.value.include === null ? 0 : 1) + control.value.exclude.length;
    case 'date-range':
    case 'number-range':
      return rangeSet(control.value.include) + rangeSet(control.value.exclude);
    case 'time-range':
      return rangeSet(control.value);
    case 'checkbox':
    case 'date':
    case 'text':
      return control.value ? 1 : 0;
    case 'boolean':
    case 'number-input':
      return control.value === null ? 0 : 1;
  }
};

export const getActiveControlsCount = (controls: FilterControl[]) =>
  controls.reduce((count, control) => count + getControlActiveCount(control), 0);

export const normalizeSearchText = (value: string) => value.toLowerCase().replace(/[^\p{Letter}\p{Number}]/gu, '');
